import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { isWithinInterval, addDays, addMonths, subDays } from 'date-fns';
import { stringify } from 'query-string';
import { useMutation } from '@tanstack/react-query';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { getPlan } from '@dashboard/services/plans';
import { formatPrice } from '@dashboard/services/formatting';

import { useToast } from '@dashboard/context/ToastContext';
import { fetcher } from '@dashboard/services/request';
import { tracking, useTracking } from '@dashboard/lib/tracking';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';

import Button from './Button';
import Dialog from './Dialog';

export default function UpgradeToYearlyDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const { seller, plan: sellerPlan, token, isAdmin } = useAuth();
  const history = useHistory();
  const { emitToast } = useToast();
  const [upgradeFlow, setUpgradeFlow] = useState(null);
  const { updateSeller } = useSellerQueries();
  useTracking();

  const currentPlan = getPlan(sellerPlan?.handle) || {};

  const changePlanMutation = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${seller._id}/change-subscription`, token, {
        method: 'POST',
        body: JSON.stringify({
          handle: currentPlan.handle,
          billingPeriod: 'year',
          upgradeFlow,
        }),
      }),
    onSuccess: () => {
      emitToast({
        title: 'Subscription updated',
        description: `You've successfully upgraded to yearly billing`,
        type: 'success',
      });

      tracking.trackEvent(`${upgradeFlow}_accepted`, {
        currentPlan: currentPlan.handle,
      });

      handleClose(true);
    },
    onError: () => {
      setError(
        'Something went wrong. Please try again on the upgrade page. You will be redirected in 5 seconds.',
      );

      setTimeout(() => {
        history.push(
          `/upgrade/subscribe?${stringify({
            plan: currentPlan.handle,
            billing_period: 'year',
          })}`,
        );
      }, 5000);
    },
  });

  useEffect(() => {
    // never show for admins
    if (isAdmin) return;

    if (!seller) return;

    const shouldShowPrompt = () => {
      // Only show for active monthly subscriptions
      if (seller.subscriptionStatus !== 'active' || seller.planBillingPeriod !== 'month') {
        return false;
      }

      const now = new Date();
      const subscriptionStart = new Date(seller.stripeSubscriptionStart);

      // Second week offer (7-14 days after start)
      const isInSecondWeekWindow = isWithinInterval(now, {
        start: addDays(subscriptionStart, 7),
        end: addDays(subscriptionStart, 14),
      });

      if (isInSecondWeekWindow) {
        if (seller.yearlyUpgradeDismissedAt) {
          const dismissedAt = new Date(seller.yearlyUpgradeDismissedAt);
          const isDismissedInWindow = isWithinInterval(dismissedAt, {
            start: addDays(subscriptionStart, 7),
            end: addDays(subscriptionStart, 31),
          });
          if (isDismissedInWindow) return false;
        }

        setUpgradeFlow('first_week_yearly_upgrade_modal');
        tracking.trackEvent('first_week_yearly_upgrade_modal_shown', {
          currentPlan: currentPlan.handle,
        });
        return true;
      }

      // First month end offer (3 days before first renewal)
      const isInFirstMonthEndWindow = isWithinInterval(now, {
        start: subDays(addMonths(subscriptionStart, 1), 3),
        end: addMonths(subscriptionStart, 1),
      });

      if (isInFirstMonthEndWindow) {
        // Show until dismissed during this period
        if (seller.yearlyUpgradeDismissedAt) {
          const dismissedAt = new Date(seller.yearlyUpgradeDismissedAt);
          const isDismissedInWindow = isWithinInterval(dismissedAt, {
            start: subDays(addMonths(subscriptionStart, 1), 3),
            end: addMonths(subscriptionStart, 1),
          });

          if (isDismissedInWindow) {
            return false;
          }
        }
        setUpgradeFlow('first_month_end_yearly_upgrade_modal');
        tracking.trackEvent('first_month_end_yearly_upgrade_modal_shown', {
          currentPlan: currentPlan.handle,
        });
        return true;
      }

      // Second month end offer (3 days before second renewal)
      const isInSecondMonthWindow = isWithinInterval(now, {
        start: subDays(addMonths(subscriptionStart, 2), 3),
        end: addMonths(subscriptionStart, 2),
      });

      if (isInSecondMonthWindow) {
        // Show until dismissed during this period
        if (seller.yearlyUpgradeDismissedAt) {
          const dismissedAt = new Date(seller.yearlyUpgradeDismissedAt);
          const isDismissedInWindow = isWithinInterval(dismissedAt, {
            start: subDays(addMonths(subscriptionStart, 2), 3),
            end: addMonths(subscriptionStart, 2),
          });

          if (isDismissedInWindow) {
            return false;
          }
        }
        setUpgradeFlow('second_month_end_yearly_upgrade_modal');
        tracking.trackEvent('second_month_end_yearly_upgrade_modal_shown', {
          currentPlan: currentPlan.handle,
        });
        return true;
      }

      return false;
    };

    const checkPromptTiming = () => {
      if (shouldShowPrompt()) {
        setIsOpen(true);
      }
    };

    // Check immediately and then every hour
    checkPromptTiming();
    const interval = setInterval(checkPromptTiming, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [seller]);

  const handleClose = async (upgraded = false) => {
    // If already closed or was an upgrade, just close without tracking
    if (!isOpen || upgraded) {
      setIsOpen(false);
      return;
    }

    // Store dismissal in database first
    await updateSeller({
      yearlyUpgradeDismissedAt: new Date(),
    });

    // Track the dismissal event
    if (upgradeFlow) {
      tracking.trackEvent(`${upgradeFlow}_dismissed`, {
        currentPlan: currentPlan.handle,
      });
    }

    // Close the dialog last
    setIsOpen(false);
  };

  const handleUpgrade = async () => {
    setError(null);
    changePlanMutation.mutate();
  };

  const totalSavings = formatPrice({
    amount: currentPlan.monthlyPrice * 12 - currentPlan.yearlyPrice * 12,
    currency: 'usd',
  });

  return (
    <Dialog
      size="lg"
      open={isOpen}
      onCancel={handleClose}
      cancelButton={false}
      confirmButton={false}
    >
      {({ close }) => (
        <div className="text-center">
          <div className="flex justify-center items-center gap-x-2 -mt-6">
            <span className="text-sm font-medium bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded relative">
              Special offer
              <div className="absolute -top-1 -left-10">
                <SparklesIcon className="w-8 h-8 text-chocolate-1000" />
              </div>
            </span>
          </div>

          <div className="my-4">
            <div className="text-2xl font-bold mb-2">Upgrade to Yearly & Save</div>
            <div className="text-lg text-gray-500">
              Get <span className="font-medium text-chocolate-1000">2 months free</span> when you
              switch to annual billing
            </div>

            <div className="flex justify-center items-start gap-x-8 mt-8">
              <div className="text-2xl font-bold pt-2 text-gray-400">
                <div className="text-base font-medium mb-0.5">Monthly plan</div>
                <div>
                  {formatPrice({ amount: currentPlan.monthlyPrice, currency: 'usd' })}
                  <span className="text-xl">/ month</span>
                </div>
              </div>

              <div className="h-24 border-r border-gray-200" />

              <div className="text-2xl font-bold pt-2">
                <div className="text-base font-medium text-chocolate-1000 mb-0.5">Yearly plan</div>
                <div className="text-2xl font-bold text-chocolate-1000">
                  {formatPrice({
                    amount: currentPlan.yearlyPrice,
                    currency: 'usd',
                  })}
                  <span className="text-xl">/ month</span>
                </div>
                <div className="text-xs text-gray-500 font-normal mt-0.5">
                  ({formatPrice({ amount: currentPlan.yearlyPrice * 12, currency: 'usd' })} billed
                  annually)
                </div>
              </div>
            </div>

            <ul className="space-y-2 text-center bg-gray-50 w-full rounded-lg p-4 mt-8">
              <li className="flex items-center">
                <CheckCircleIcon className="w-4 h-4 text-chocolate-600 mr-2" />
                Save 16% compared to monthly billing
              </li>
              <li className="flex items-center">
                <CheckCircleIcon className="w-4 h-4 text-chocolate-600 mr-2" />
                Lock in current pricing for a full year
              </li>
              <li className="flex items-center">
                <CheckCircleIcon className="w-4 h-4 text-chocolate-600 mr-2" />
                Keep access to the same features
              </li>
            </ul>

            <div className="mt-8 flex flex-col space-y-4">
              <Button
                onClick={handleUpgrade}
                appearance="primary"
                size="full"
                disabled={changePlanMutation.isLoading}
              >
                {changePlanMutation.isLoading
                  ? 'Upgrading...'
                  : `Upgrade to Yearly and save ${totalSavings}`}
              </Button>

              {error && <div className="text-red-600 text-sm text-center">{error}</div>}

              <Button onClick={close} appearance="link" className="text-center mx-auto">
                No thanks
              </Button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
